import { styled } from '@mui/material/styles';
import {
  Box,
  TextField,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Modal,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Avatar,
  Popover,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Rating,
  List,
  ListItem,
} from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import { colorSchemes } from '../shared-theme/themePrimitives';
import { LinkProps } from 'react-router-dom';
// ============================================================================
// GLASSMORPHISM DESIGN SYSTEM
// Based on Figma specifications with beige background and complex glass effects
// ============================================================================

// Color Palette - Updated with theme support
export const GlassColors = {
  light: {
    primaryBackground: '#E5E5DC', // Beige
    secondary: '#005CAD', // Blue
    accent: '#00E8E7', // Cyan
    textPrimary: '#1A1A1A', // Dark Gray
    textSecondary: '#393939', // Medium Gray
    textMuted: '#585858', // Light Gray
    buttonBackground: 'rgba(0, 92, 173, 0.9)',
    iconBtnBackground: 'rgba(0, 36, 58, 0.8)',
    inputBackground: 'rgba(153, 153, 153, 0.3)',
  },
  dark: {
    primaryBackground: '#1A1A1A', // Dark
    secondary: '#005CAD', // Blue
    accent: '#00E8E7', // Cyan
    textPrimary: '#D5D5D5', // Light Beige
    textSecondary: '#B6B6B6', // Light Beige
    textMuted: 'rgba(176, 176, 176, 1)', // Light Gray
    buttonBackground: 'rgba(0, 92, 173, 0.9)',
    iconBtnBackground: 'rgba(0, 92, 173, 0.7)',
    inputBackground: 'rgba(153, 153, 153, 0.3)',
  },
  // Legacy colors for backward compatibility
  primaryBackground: '#E5E5DC',
  secondary: '#005CAD',
  accent: '#00E8E7',
  textPrimary: '#1A1A1A',
  textSecondary: '#393939',
  textMuted: '#585858',
  buttonBackground: 'rgba(59, 59, 59, 0.18)',
  tertiary: 'rgba(132, 132, 132, 0.3)',
  additional: 'rgba(41, 163, 34, 1)',
};

// Typography Specifications - Updated with exact design specs
export const GlassTypography = {
  heading: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
  },
  headingBold: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 800,
  },
  body: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
  },
  bodyMedium: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
  },
  button: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
  },
  // New typography variants from design specs
  hero: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '60px',
    fontWeight: 500,
    lineHeight: '120%',
  },
  categoryTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '20px',
    fontWeight: 800,
    lineHeight: '160%',
  },
  subtitle1: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '175%',
  },
  subtitle2: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '157%',
  },
  caption: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '166%',
  },
};

// Responsive Breakpoints
export const GlassBreakpoints = {
  desktop: '1920px',
  laptop: '1200px',
  tablet: '768px',
  mobile: '425px',
};

// Complex Glass Shadow Effects - Updated with exact specifications
export const GlassShadows = {
  // Standard "Up 1" Glass Effect (exact from design specs)
  glassUp: `
    0px 0px 16px 0px rgba(92, 92, 92, 0.3) inset,
    0px 0px 3px 0px rgba(255, 255, 255, 0.4) inset,
    -1px -1px 0.5px -1px rgba(255, 255, 255, 1) inset,
    1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
    1px -1px 0px -0.5px rgba(255, 255, 255, 0.5) inset,
    1px 1px 0px -0.5px rgba(255, 255, 255, 0.6) inset,
    0px 1px 8px 0px rgba(0, 0, 0, 0.12),
    0px 0px 2px 0px rgba(0, 0, 0, 0.1)
  `,

  buttonShadow: `
    0px 0px 16px 0px rgba(242, 242, 242, 0.1) inset,
    0px 0px 3px 0px rgba(255, 255, 255, 0.3) inset,
    -1px -1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
    1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
    -1px -1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
    1px 1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
    0px 1px 8px 0px rgba(0, 0, 0, 0.12),
    0px 0px 2px 0px rgba(0, 0, 0, 0.1)
  `,

  buttonActive: `
    0px 0px 3px 0px rgba(0, 232, 231, 0.34),
    1px 1px 0px -1px rgba(255, 255, 255, 1) inset,
    1px 1px 0px -0.5px rgba(225, 225, 225, 0.6),
    0px 1px 4px 0px rgba(0, 0, 0, 0.3) inset,
    0px 0px 2px 0px rgba(0, 0, 0, 0.1) inset
  `,
  buttonHover: `
    0px 0px 16px 0px rgba(0, 23, 62, 0.4) inset,
    0px 0px 3px 0px rgba(0, 73, 137, 0.5) inset,
    -1px -1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
    1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
    -1px -1px 0px -0.5px rgba(255, 255, 255, 0.3) inset,
    1px 1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
    0px 1px 8px 0px rgba(0, 255, 255, 0.2),
    0px 0px 2px 0px rgba(0, 0, 0, 0.1)
  `,

  // Footer "Down 1" Glass Effect (exact from design specs)
  glassDown: `
    inset 5px 5px 24px -11.7px rgba(0, 0, 0, 0.1),
    inset -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.04),
    inset 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.2),
    inset -2.82453px -2.82453px 8.49057px -5px rgba(255, 255, 255, 0.48)
  `,

  additionalCard: `
    -2.82px -2.82px 8.49px 0px rgba(255, 255, 255, 0.3),
    14.15px 14.15px 22.64px -5.37px rgba(0, 0, 0, 0.1),
    -7.54px -7.54px 8.66px 0px rgba(255, 255, 255, 0.2),
    5px 5px 24px -11.7px rgba(0, 0, 0, 0.2)
  `,

  // Button Secondary Effect (exact from design specs)
  buttonSecondary: `
    0px 0px 2px rgba(0, 0, 0, 0.1),
    0px 1px 8px rgba(0, 0, 0, 0.12),
    inset 1px 1px 0px -0.5px #333333,
    inset -1px -1px 0px -0.5px #262626,
    inset 1px 1px 0.5px -1px #FFFFFF,
    inset -1px -1px 0.5px -1px #FFFFFF,
    inset 0px 0px 3px rgba(255, 255, 255, 0.5),
    inset 0px 0px 16px #F2F2F2
  `,

  // Blue Navigation Glass Effect
  glassBlue: `
    10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2),
    -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.3),
    14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1),
    -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.6),
    inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 92, 173, 0.3),
    inset 1.57925px 1.57925px 1.69811px rgba(255, 255, 255, 0.8)
  `,

  menuMobile: ` 
    -2.82px -2.82px 8.49px 0px rgba(255, 255, 255, 0.3),
    14.15px 14.15px 22.64px -0.37px rgba(0, 0, 0, 0.1),
    -17.54px -17.54px 28.66px 0px rgba(255, 255, 255, 0.05),
    10px 10px 28.66px -11.7px rgba(0, 0, 0, 0.2),
    1.58px 1.58px 1.7px 0px rgba(255, 255, 255, 1) inset,
    -2.07px -2.64px 4.52px -1.51px rgba(0, 0, 0, 0.3) inset`,

  cardShadowDefault: `
    -2.82px -2.82px 8.49px 0px #FFFFFF4D,
    14.15px 14.15px 22.64px -0.37px #0000001A,
    -17.54px -17.54px 28.66px 0px #FFFFFF0D,
    10px 10px 28.66px -11.7px #00000033,
    1.58px 1.58px 1.7px 0px #FFFFFF inset,
    -2.07px -2.64px 4.52px -1.51px #0000004D inset
  `,
  cardShadowActive: `
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    14.15px 14.15px 22.64px -0.37px #00FFFF21 inset,
    -17.54px -17.54px 28.66px 0px #A6FFFF4D inset,
    10px 10px 28.66px -11.7px #00929233 inset,
    1.58px 1.58px 1.7px 0px #A3FFFF,
    -3.07px -4px 2px -1.51px #00929238
  `,

  cardShadowHover: `
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    0px 0px 0px 0px #00000000,
    1.58px 1.58px 1.7px 0px #A3FFFF inset,
    -2.07px -2.64px 4.52px -1.51px #00929252 inset,
    0px 0px 0px 0px #00000000 inset,
    14.15px 14.15px 22.64px -0.37px #00FFFF21,
    -17.54px -17.54px 28.66px 0px #A6FFFF30
  `,
};

// ============================================================================
// MAIN CONTAINER COMPONENTS
// ============================================================================

export const GlassContainer = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight ? GlassColors.light.primaryBackground : '#1A1A1A';
  const gradientColor = isLight ? 'rgba(229, 229, 220, 0.9)' : '#1A1A1A';

  return {
    backgroundColor: bgColor,
    // minHeight: '100vh',
    // backgroundImage: isLight
    //   ? `linear-gradient(135deg, ${bgColor} 0%, ${gradientColor} 100%)`
    //   : 'none',
    backdropFilter: 'blur(6px)',
    position: 'relative',
    // Mobile scroll optimization
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    // Fix iOS viewport height issues - use dvh where supported
    '@supports (height: 100dvh)': {
      // minHeight: '100dvh',
    },
    // Prevent zoom on iOS when focusing inputs
    '@media (max-width: 768px)': {
      fontSize: '16px', // Prevent iOS zoom
    },
  };
});

export const GlassCard = styled(Box)(({ theme }) => ({
  borderRadius: '64px',
  boxShadow: GlassShadows.cardShadowDefault,
  padding: theme.spacing(3),
  position: 'relative',
  overflow: 'visible',
  // Mobile optimization
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '24px',
    padding: theme.spacing(2),
  },
}));

export const GlassHeroContainer = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight ? GlassColors.light.primaryBackground : '#1A1A1A';
  // const gradientColor = isLight ? 'rgba(229, 229, 220, 0.95)' : '#1A1A1A';

  return {
    // backgroundColor: bgColor,
    // backgroundImage: isLight
    //   ? `
    //   radial-gradient(ellipse 80% 50% at 50% -20%, rgba(0, 232, 231, 0.1), transparent),
    //   linear-gradient(135deg, ${bgColor} 0%, ${gradientColor} 100%)
    // `
    //   : 'none',
    backdropFilter: 'blur(6px)',
    position: 'relative',
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
  };
});

// ============================================================================
// NAVIGATION COMPONENTS (Header)
// ============================================================================

export const GlassAppBar = styled(AppBar)(({ theme }) => {
  // Always use light mode color - never change with theme
  const backgroundColor = '#00243ACC';

  return {
    backgroundColor: `${backgroundColor} !important`,
    backdropFilter: 'blur(6px)',
    borderRadius: '0 0 32px 32px',
    boxShadow:
      '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
    border: 'none',
  };
});

export const GlassToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  minHeight: '72px',
}));

export const GlassNavButton = styled(Button)`
  && {
    font-family: Montserrat, sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: ${GlassColors.textPrimary} !important;
    padding: 8px 16px;
    border-radius: 32px;
    text-transform: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      transform: scale(1.05);
      color: ${GlassColors.textPrimary} !important;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
    }

    & span {
      color: ${GlassColors.textPrimary} !important;
    }
  }
`;

export const GlassNavButtonPrimary = styled(Button)`
  && {
    font-family: Montserrat, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: ${GlassColors.textPrimary} !important;
    padding: 8px 16px;
    border-radius: 32px;
    text-transform: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(0, 232, 231, 0.1) !important;
      transform: scale(1.05);
      color: #00e8e7 !important;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
      box-shadow: 0 0 20px rgba(0, 232, 231, 0.3);
    }

    & span {
      color: ${GlassColors.textPrimary} !important;
    }
  }
`;

export const GlassNavButtonSecondary = styled(Button)`
  && {
    font-family: Montserrat, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: ${GlassColors.textPrimary} !important;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(6px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 8px 16px;
    border-radius: 32px;
    text-transform: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2) !important;
      transform: scale(1.05);
      color: ${GlassColors.textPrimary} !important;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
    }

    & span {
      color: ${GlassColors.textPrimary} !important;
    }
  }
`;

export const GlassIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  color: theme.palette.mode === 'dark' ? '#D5D5D5' : GlassColors.textPrimary,
  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.1)',
    color: theme.palette.mode === 'dark' ? '#D5D5D5' : GlassColors.textPrimary,
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))',
  },

  '& svg': {
    color: theme.palette.mode === 'dark' ? '#D5D5D5' : GlassColors.textPrimary,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))',
  },
}));

export const GlassLogo = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))',
  },
}));

// ============================================================================
// FORM COMPONENTS
// ============================================================================

export const GlassTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: GlassColors.tertiary,
    borderRadius: '24px',
    height: 'auto',
    lineHeight: '20px',
    border: 0,
    fontSize: '14px',
    fontFamily: '"Montserrat", sans-serif',
    boxShadow:
      '0px 0px 10px 0px rgba(0, 0, 0, 0.04), 0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3), 0px 0.5px 0.75px 0px rgba(255, 255, 255, 1), 0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset, 0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
    },
  },
  color:
    theme.palette.mode === 'dark'
      ? GlassColors.dark.textPrimary
      : GlassColors.light.textPrimary,
  '& .MuiInputBase-input::placeholder': {
    color:
      theme.palette.mode === 'dark'
        ? GlassColors.dark.textSecondary
        : GlassColors.light.textSecondary,
    opacity: 1,
  },
  '&& input:-webkit-autofill': {
    WebkitBoxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 0 1000px #3a3a3a inset'
        : '0 0 0 1000px #c8c8c2 inset',
    WebkitTextFillColor:
      theme.palette.mode === 'dark'
        ? GlassColors.dark.textPrimary
        : GlassColors.light.textPrimary,
  },
  '& input:-webkit-autofill:focus': {
    WebkitBoxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 0 1000px #3a3a3a inset'
        : '0 0 0 1000px #c8c8c2 inset',
  },
}));

type GlassRouterButtonProps = ButtonProps | LinkProps;
export const GlassButton = styled(Button)<GlassRouterButtonProps>(
  ({ theme }) => ({
    '&&': {
      ...GlassTypography.button,
      fontSize: '14px',
      height: 'auto',
      lineHeight: 2,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? GlassColors.dark.iconBtnBackground
          : GlassColors.light.iconBtnBackground,
      color: '#E2F7FF',
      padding: '6px 12px',
      border: 0,
      backgroundImage: 'none',
      borderRadius: '32px',
      textTransform: 'none',
      fontWeight: 500,
      boxShadow: GlassShadows.buttonShadow,
      transition: 'box-shadow 0.3s ease',
      [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
      },
    },

    '&:hover': {
      backgroundColor: GlassColors.light.iconBtnBackground,
      boxShadow: GlassShadows.buttonHover,
    },
    '&[disabled]': {
      backgroundColor: `${GlassColors.buttonBackground}`,
      backgroundImage: 'none',
      color: '#777777',
      textShadow: '0px 1px 1px #00000021',
    },
  }),
);

export const ButtonTertiaryIcon = styled(Button)(({ theme }) => ({
  fontSize: '24px',
  width: '40px',
  height: '40px',
  minWidth: 'unset',
  backgroundColor: GlassColors.buttonBackground,
  padding: '8px',
  borderRadius: 99999,
  backdropFilter: 'blur(6px)',
  border: 'none',
  boxShadow: GlassShadows.glassUp,
  transition: 'box-shadow 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    '& svg': {
      fontSize: '16px',
    },
  },
  '& path': {
    fill:
      theme.palette.mode === 'dark'
        ? GlassColors.dark.textSecondary
        : GlassColors.textPrimary,
    transition: 'fill .2s ease',
  },
  '&&:hover': {
    boxShadow: GlassShadows.buttonHover,
    backgroundColor: GlassColors.buttonBackground,
    '&& path': {
      fill: GlassColors.secondary,
    },
  },
  '&:active': {
    boxShadow: GlassShadows.buttonActive,
    backgroundColor: GlassColors.buttonBackground,
    '& path': {
      fill: GlassColors.secondary,
    },
  },
}));

export const ButtonTertiary = styled(Button)(({ theme }) => ({
  '&&': {
    fontSize: '12px',
    lineHeight: '24px',
    padding: '4px 12px',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
    minWidth: 'unset',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? GlassColors.tertiary
        : GlassColors.buttonBackground,
    borderRadius: '32px',
    backdropFilter: 'blur(6px)',
    border: 'none',
    height: 'auto',
    boxShadow: GlassShadows.glassUp,
    transition: 'box-shadow 0.2s ease',
  },

  '&': {
    color:
      theme.palette.mode === 'dark'
        ? GlassColors.dark.textSecondary
        : GlassColors.textSecondary,
  },
  [theme.breakpoints.down('sm')]: {
    '& svg': {
      fontSize: '16px',
    },
  },
  '& path': {
    fill: GlassColors.textPrimary,
    transition: 'fill .2s ease',
  },
  '&:hover': {
    boxShadow: GlassShadows.buttonHover,
    backgroundColor: GlassColors.buttonBackground,
    color: GlassColors.secondary,
    '& path': {
      fill: GlassColors.secondary,
    },
  },
  '& svg': { transition: 'transform .2s' },
  '&:hover svg': {
    // transform: 'translateX(-3px)',
  },
  '&:active': {
    boxShadow: GlassShadows.buttonActive,
    backgroundColor: GlassColors.buttonBackground,
    color: GlassColors.secondary,
    '& path': {
      fill: GlassColors.secondary,
    },
  },
}));

export const GlassButtonSecondary = styled(Button)(({ theme }) => ({
  ...GlassTypography.button,
  fontSize: '16px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(255, 255, 255, 0.2)',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.2) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
  color: theme.palette.mode === 'dark' ? '#D5D5D5' : GlassColors.textPrimary,
  padding: theme.spacing(1.5, 3),
  borderRadius: '32px',
  textTransform: 'none',
  fontWeight: 500,
  backdropFilter: 'blur(6px)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: GlassShadows.glassUp,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.4)'
        : 'rgba(255, 255, 255, 0.3)',
    boxShadow: `${GlassShadows.glassUp}, 0 10px 20px rgba(0, 0, 0, 0.1)`,
  },
}));

// ============================================================================
// DROPDOWN AND INTERACTIVE COMPONENTS
// ============================================================================

export const GlassDropdownBox = styled(Box)(({ theme }) => ({
  maxHeight: '324px',
  borderRadius: '32px',
  overflowY: 'auto',
  zIndex: 999,
  '&::-webkit-scrollbar': {
    background: 'transparent',
    width: '12px',
    zIndex: '-1',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    marginBlock: '29px',
    // boxShadow:
    //   '0px 0px 34.77px 0px rgba(0, 0, 0, 0.05) inset, 0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.5) inset, 0px 0.5px 0.75px 0px rgba(255, 255, 255, 1) inset, 0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.3), 0px 2.08px 2.48px -2px rgba(0, 0, 0, 0.4), 0px 3.08px 2px -2px rgba(0, 0, 0, 0.33)',
  },
  '&::-webkit-scrollbar-thumb': {
    borderLeft: '3px solid transparent',
    borderRight: '3px solid transparent',
    background: 'rgba(0, 92, 173, 1)',
    borderRadius: '6px',
    height: '54px',
    backgroundClip: 'content-box',
    // boxShadow: '1px 2px 4px rgba(0,0,0,0.25) inset',
    boxShadow: `inset 0 1px 1px rgba(255,255,255,0.45),
  inset 0 -1px 1px rgba(0, 0, 0, 0.48),
  inset 0 0px 2px #ffffffff,
  inset 1px 2px 4px rgba(0, 0, 0, 0.25)`,
  },
}));

export const GlassDropdownItem = styled(Box)(({ theme }) => ({
  padding: '8px 32px',
  cursor: 'pointer',
  fontFamily: 'Roboto, sans-serif',
  letterSpacing: '.15px',
  lineHeight: '150%',
  color:
    theme.palette.mode === 'dark'
      ? GlassColors.primaryBackground
      : GlassColors.textPrimary,
  transition: 'all 0.3s ease',
  borderRadius: '32px',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? GlassColors.dark.iconBtnBackground
        : GlassColors.light.iconBtnBackground,
    color: 'rgba(229, 229, 220, 1)',
  },
}));

export const GlassChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(6px)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '32px',
  padding: theme.spacing(0.5, 1.5),
  margin: theme.spacing(0.5),
  ...GlassTypography.body,
  fontSize: '12px',
  color: theme.palette.mode === 'dark' ? '#D5D5D5' : GlassColors.textPrimary,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(0, 232, 231, 0.2)',
    transform: 'scale(1.05)',
    color: GlassColors.secondary,
  },
}));

// ============================================================================
// TYPOGRAPHY COMPONENTS
// ============================================================================

export const HeadingText = styled(Typography, {
  shouldForwardProp: (prop) => !['component'].includes(prop as string),
})<{
  component?: React.ElementType;
}>(({ theme }) => ({
  ...GlassTypography.heading,
  fontSize: 'clamp(3rem, 10vw, 4rem)',
  lineHeight: 1.2,
  color: GlassColors.textPrimary,
  textAlign: 'center',
  fontWeight: 500,
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}));

export const SectionHeader = styled(Typography)(({ theme }) => ({
  ...GlassTypography.headingBold,
  fontSize: '20px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : GlassColors.textPrimary,
  fontWeight: 800,
  marginBottom: theme.spacing(2),
}));

export const BodyText = styled(Typography)(({ theme }) => ({
  ...GlassTypography.body,
  fontSize: '16px',
  color:
    theme.palette.mode === 'dark'
      ? GlassColors.dark.textSecondary
      : GlassColors.textSecondary,
  lineHeight: 1.6,
}));

export const CaptionText = styled(Typography)(({ theme }) => ({
  ...GlassTypography.button,
  fontSize: '12px',
  color: GlassColors.textMuted,
  fontWeight: 500,
}));

// ============================================================================
// FOOTER COMPONENTS
// ============================================================================

export const GlassFooter = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight
    ? GlassColors.light.primaryBackground
    : GlassColors.dark.primaryBackground;
  const gradientColor = isLight
    ? 'rgba(229, 229, 220, 0.95)'
    : 'rgba(26, 26, 26, 0.95)';

  return {
    backgroundColor: bgColor,
    backgroundImage: `linear-gradient(135deg, ${bgColor} 0%, ${gradientColor} 100%)`,
    backdropFilter: 'blur(6px)',
    boxShadow: GlassShadows.glassDown,
    padding: theme.spacing(8, 0),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background:
        'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    },
  };
});

export const FooterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const FooterSectionTitle = styled(Typography)(({ theme }) => ({
  ...GlassTypography.headingBold,
  fontSize: '16px',
  color: GlassColors.textPrimary,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

export const FooterLink = styled(Typography, {
  shouldForwardProp: (prop) => !['component', 'href'].includes(prop as string),
})<{
  component?: React.ElementType;
  href?: string;
}>(({ theme }) => ({
  ...GlassTypography.body,
  fontSize: '14px',
  color: GlassColors.textSecondary,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  textDecoration: 'none',
  '&:hover': {
    color: GlassColors.accent,
    textDecoration: 'underline',
  },
}));

export const FooterLogo = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const FooterCompanyName = styled(Typography)(({ theme }) => ({
  ...GlassTypography.headingBold,
  fontSize: '24px',
  color: GlassColors.textPrimary,
  fontWeight: 700,
  marginBottom: theme.spacing(1),
}));

export const GlassSocialIcon = styled(IconButton, {
  shouldForwardProp: (prop) => !['href'].includes(prop as string),
})<{
  href?: string;
}>(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '50%',
  color: GlassColors.textSecondary,
  width: '40px',
  height: '40px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: GlassColors.accent,
    color: 'white',
    transform: 'scale(1.1)',
    boxShadow: `0 0 20px rgba(0, 232, 231, 0.4)`,
  },
}));

// ============================================================================
// CATEGORY GRID COMPONENTS
// ============================================================================

export const GlassCategoryCard = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  return {
    borderRadius: '32px',
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: GlassShadows.additionalCard,
    '&:hover': {
      boxShadow: GlassShadows.cardShadowHover,
    },
    '&:active': {
      boxShadow: GlassShadows.cardShadowActive,
    },
  };
});

export const GlassCategoryIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  fontSize: '40px',
  color: GlassColors.secondary,
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    width: '72px',
    height: '72px',
  },
}));

export const GlassCategoryTitle = styled(Typography)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  return {
    ...GlassTypography.headingBold,
    fontSize: '16px',
    color: isLight ? GlassColors.textPrimary : GlassColors.dark.textSecondary,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  };
});

export const GlassCategoryDescription = styled(Typography)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';

  return {
    ...GlassTypography.body,
    fontSize: '14px',
    color: isLight ? GlassColors.textPrimary : GlassColors.dark.textSecondary,
    lineHeight: 1.4,
  };
});

// ============================================================================
// USER PROFILE SPECIFIC COMPONENTS
// ============================================================================

export const GlassModal = styled(Modal)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(8px)',
  },
}));

export const GlassModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor:
    theme.palette.mode === 'light'
      ? GlassColors.light.primaryBackground
      : '#1A1A1A',
  backdropFilter: 'blur(6px)',
  borderRadius: '32px',
  boxShadow: GlassShadows.menuMobile,
  padding: theme.spacing(4),
  maxHeight: '80vh',
  overflowY: 'auto',
  minWidth: '400px',
  width: '90vw',
  maxWidth: '500px',
  '&:focus': {
    outline: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '95vw',
    maxWidth: '95vw',
    minWidth: 'auto',
    padding: theme.spacing(2),
    borderRadius: '24px',
    maxHeight: '90vh',
  },
}));

export const GlassTab = styled(Tab)(({ theme }) => ({
  ...GlassTypography.button,
  fontSize: '14px',
  fontWeight: 500,
  color: GlassColors.textSecondary,
  textTransform: 'none',
  borderRadius: '32px',
  padding: theme.spacing(1, 2),
  margin: theme.spacing(0, 0.5),
  transition: 'all 0.3s ease',
  '&.Mui-selected': {
    color: GlassColors.textPrimary,
    backgroundColor: 'rgba(0, 232, 231, 0.1)',
    boxShadow: `0 0 20px rgba(0, 232, 231, 0.2)`,
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'scale(1.02)',
  },
}));

export const GlassTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'none', // Hide default indicator since we use custom selection styling
  },
  '& .MuiTabs-flexContainer': {
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

export const GlassAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(6px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: GlassShadows.glassUp,
  ...GlassTypography.body,
  fontSize: '14px',
  '&.MuiAlert-standardSuccess': {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    color: GlassColors.textPrimary,
  },
  '&.MuiAlert-standardError': {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    color:
      theme.palette.mode === 'dark'
        ? GlassColors.primaryBackground
        : GlassColors.textPrimary,
  },
}));

export const GlassSnackbar = styled(Snackbar)(({ theme }) => ({
  '& .MuiSnackbarContent-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
  },
}));

export const GlassAvatar = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '& .MuiAvatar-root': {
    border: '3px solid rgba(255, 255, 255, 0.3)',
    boxShadow: GlassShadows.glassUp,
  },
}));

export const GlassAvatarCamera = styled(IconButton)`
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: ${GlassColors.textPrimary};
  box-shadow: ${GlassShadows.glassUp};
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(0, 232, 231, 0.2);
    transform: scale(1.05);
    color: ${GlassColors.accent};
    box-shadow:
      ${GlassShadows.glassUp},
      0 0 20px rgba(0, 232, 231, 0.3);
  }
`;

export const GlassInfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const GlassInfoIcon = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 92, 173, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: GlassColors.secondary,
  flexShrink: 0,
}));

export const GlassInfoText = styled(Box)(({ theme }) => ({
  flex: 1,
  '& .info-label': {
    ...GlassTypography.button,
    fontSize: '14px',
    fontWeight: 600,
    color: GlassColors.textPrimary,
    marginBottom: theme.spacing(0.5),
  },
  '& .info-value': {
    ...GlassTypography.body,
    fontSize: '14px',
    color: GlassColors.textSecondary,
  },
}));

// ============================================================================
// POP-UP & MODAL COMPONENTS
// ============================================================================

export const GlassPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(6px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: GlassShadows.glassUp,
    padding: 0,
    overflow: 'visible',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)`,
      borderRadius: 'inherit',
      pointerEvents: 'none',
    },
  },
}));

export const GlassPopoverContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
}));

export const GlassImageModal = styled(Modal)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(12px)',
  },
}));

export const GlassImageModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  backdropFilter: 'blur(6px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: `${GlassShadows.glassUp}, 0 25px 50px rgba(0, 0, 0, 0.5)`,
  padding: 0,
  overflow: 'hidden',
  maxWidth: '90vw',
  maxHeight: '90vh',
  '&:focus': {
    outline: 'none',
  },
}));

export const GlassCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  color: 'white',
  zIndex: 10,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(244, 67, 54, 0.8)',
    transform: 'scale(1.1)',
    boxShadow: '0 0 20px rgba(244, 67, 54, 0.5)',
  },
}));

// ============================================================================
// CHAT INTERFACE COMPONENTS
// ============================================================================

export const GlassMessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isOwn',
})<{ isOwn: boolean }>(({ theme, isOwn }) => ({
  backgroundColor: isOwn ? 'rgba(0, 92, 173, 0.9)' : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(6px)',
  border: `1px solid ${
    isOwn ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 92, 173, 0.2)'
  }`,
  borderRadius: isOwn
    ? `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(
        1,
      )} ${theme.spacing(3)}`
    : `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(
        3,
      )} ${theme.spacing(1)}`,
  padding: theme.spacing(1.5, 2),
  maxWidth: '85%',
  alignSelf: isOwn ? 'flex-end' : 'flex-start',
  color: isOwn ? 'white' : GlassColors.textPrimary,
  boxShadow: isOwn ? GlassShadows.glassBlue : GlassShadows.glassUp,
  transition: 'all 0.3s ease',
  position: 'relative',
  marginBottom: theme.spacing(0.5),
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: isOwn
      ? `${GlassShadows.glassBlue}, 0 8px 16px rgba(0, 92, 173, 0.3)`
      : `${GlassShadows.glassUp}, 0 8px 16px rgba(0, 0, 0, 0.1)`,
  },
}));

export const GlassChatItem = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '16px',
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: `inset 0 1px 3px rgba(255, 255, 255, 0.3), 0 8px 16px rgba(0, 0, 0, 0.1)`,
  },
  '&.active': {
    backgroundColor: 'rgba(0, 232, 231, 0.2)',
    borderColor: GlassColors.accent,
    boxShadow: `inset 0 1px 3px rgba(0, 232, 231, 0.3), 0 0 20px rgba(0, 232, 231, 0.2)`,
  },
}));

export const GlassChatHeader = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.2)',
}));

export const GlassAttachment = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '12px',
  padding: theme.spacing(1),
  margin: theme.spacing(1, 0),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'scale(1.02)',
  },
}));

// ============================================================================
// CARD COMPONENTS
// ============================================================================

export const GlassHandymanCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.18)',
  backdropFilter: 'blur(8px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: GlassShadows.glassUp,
  marginBottom: theme.spacing(2),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'visible',
  position: 'relative',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)`,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.28)',
    transform: 'translateY(-6px) scale(1.02)',
    boxShadow: `${GlassShadows.glassUp}, 0 25px 50px rgba(0, 0, 0, 0.15), 0 0 30px rgba(0, 232, 231, 0.1)`,
  },
  '&:active': {
    transform: 'translateY(-2px) scale(1.01)',
  },
}));

export const GlassHandymanCardHeader = styled(CardHeader)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  '& .MuiCardHeader-avatar': {
    '& .MuiAvatar-root': {
      border: '3px solid rgba(255, 255, 255, 0.3)',
      boxShadow: GlassShadows.glassUp,
    },
  },
  '& .MuiCardHeader-title': {
    ...GlassTypography.headingBold,
    fontSize: '18px',
    color: GlassColors.textPrimary,
    fontWeight: 600,
  },
  '& .MuiCardHeader-subheader': {
    ...GlassTypography.body,
    fontSize: '14px',
    color: GlassColors.textSecondary,
  },
}));

export const GlassHandymanCardContent = styled(CardContent)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: theme.spacing(0, 3, 3, 3),
}));

export const GlassReviewCard = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.16)',
  backdropFilter: 'blur(8px)',
  borderRadius: '18px',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  boxShadow: GlassShadows.glassUp,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)`,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
    transform: 'translateY(-3px)',
    boxShadow: `${GlassShadows.glassUp}, 0 14px 28px rgba(0, 0, 0, 0.12), 0 0 15px rgba(0, 232, 231, 0.06)`,
  },
}));

export const GlassOrderCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.18)',
  backdropFilter: 'blur(8px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: GlassShadows.glassUp,
  marginBottom: theme.spacing(2),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'visible',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)`,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-4px)',
    boxShadow: `${GlassShadows.glassUp}, 0 16px 32px rgba(0, 0, 0, 0.12), 0 0 20px rgba(0, 232, 231, 0.08)`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
}));

export const GlassStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status?: string }>(({ theme, status }) => {
  let backgroundColor = 'rgba(117, 117, 117, 0.2)';
  let borderColor = 'rgba(117, 117, 117, 0.3)';
  let color = GlassColors.textPrimary;

  switch (status?.toLowerCase()) {
    case 'new':
      backgroundColor = 'rgba(33, 150, 243, 0.2)';
      borderColor = 'rgba(33, 150, 243, 0.3)';
      break;
    case 'inprogress':
    case 'in progress':
      backgroundColor = 'rgba(76, 175, 80, 0.2)';
      borderColor = 'rgba(76, 175, 80, 0.3)';
      break;
    case 'markasdone':
    case 'mark as done':
      backgroundColor = 'rgba(255, 152, 0, 0.2)';
      borderColor = 'rgba(255, 152, 0, 0.3)';
      break;
    case 'completed':
      backgroundColor = 'rgba(0, 92, 173, 0.2)';
      borderColor = 'rgba(0, 92, 173, 0.3)';
      break;
    case 'cancelled':
    case 'canceled':
      backgroundColor = 'rgba(158, 158, 158, 0.2)';
      borderColor = 'rgba(158, 158, 158, 0.3)';
      break;
    case 'verified':
      backgroundColor = 'rgba(76, 175, 80, 0.2)';
      borderColor = 'rgba(76, 175, 80, 0.3)';
      break;
  }

  return {
    backgroundColor,
    backdropFilter: 'blur(6px)',
    border: `1px solid ${borderColor}`,
    borderRadius: '16px',
    color,
    ...GlassTypography.button,
    fontSize: '12px',
    fontWeight: 600,
    height: '28px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: backgroundColor.replace('0.2', '0.3'),
      transform: 'scale(1.05)',
      boxShadow: `0 4px 8px ${borderColor}`,
    },
    '& .MuiChip-label': {
      padding: theme.spacing(0, 1),
    },
  };
});

export const GlassRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: GlassColors.accent,
    filter: 'drop-shadow(0 2px 4px rgba(0, 232, 231, 0.3))',
  },
  '& .MuiRating-iconEmpty': {
    color: 'rgba(255, 255, 255, 0.3)',
  },
}));

// ============================================================================
// ADDITIONAL CARD VARIANTS
// ============================================================================

export const GlassProposalCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#E5E5DC',
  backdropFilter: 'blur(8px)',
  borderRadius: '32px',
  border: 'none',
  boxShadow: `
    5px 5px 24px -11.7px rgba(0, 0, 0, 0.2),
    -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2),
    14.15px 14.15px 22.64px -5.37px rgba(0, 0, 0, 0.1),
    -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)
  `,
  marginBottom: theme.spacing(2),
  transition: 'transform 0.2s ease',
  position: 'relative',
  overflow: 'visible',
  cursor: 'pointer',
  padding: '16px 24px 24px',
  '&::before': {
    display: 'none',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  '&.pending': {
    borderColor: 'rgba(255, 193, 7, 0.4)',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      background:
        'linear-gradient(135deg, rgba(255, 193, 7, 0.1), transparent)',
      borderRadius: 'inherit',
      pointerEvents: 'none',
    },
  },
  '&.accepted': {
    borderColor: 'rgba(76, 175, 80, 0.4)',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      background:
        'linear-gradient(135deg, rgba(76, 175, 80, 0.1), transparent)',
      borderRadius: 'inherit',
      pointerEvents: 'none',
    },
  },
  '&.rejected': {
    borderColor: 'rgba(244, 67, 54, 0.4)',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      background:
        'linear-gradient(135deg, rgba(244, 67, 54, 0.1), transparent)',
      borderRadius: 'inherit',
      pointerEvents: 'none',
    },
  },
  '&.cancelled': {
    borderColor: 'rgba(158, 158, 158, 0.4)',
    opacity: 0.7,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      background:
        'linear-gradient(135deg, rgba(158, 158, 158, 0.1), transparent)',
      borderRadius: 'inherit',
      pointerEvents: 'none',
    },
  },
}));

export const GlassInfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.14)',
  backdropFilter: 'blur(8px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: GlassShadows.glassUp,
  padding: theme.spacing(2.5),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%)`,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    transform: 'translateY(-2px)',
    boxShadow: `${GlassShadows.glassUp}, 0 12px 24px rgba(0, 0, 0, 0.1)`,
  },
}));

export const GlassTestimonialCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.18)',
  backdropFilter: 'blur(8px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: GlassShadows.glassUp,
  padding: theme.spacing(3),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'visible',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)`,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-6px) scale(1.02)',
    boxShadow: `${GlassShadows.glassUp}, 0 25px 50px rgba(0, 0, 0, 0.15), 0 0 30px rgba(0, 232, 231, 0.1)`,
  },
}));

// ============================================================================
// NOTIFICATION COMPONENTS
// ============================================================================

export const GlassNotificationList = styled(List)(({ theme }) => ({
  padding: 0,
  maxHeight: '400px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 92, 173, 0.3)',
    borderRadius: '3px',
  },
}));

export const GlassNotificationItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'unread',
})<{ unread?: boolean }>(({ theme, unread }) => ({
  backgroundColor: unread ? 'rgba(0, 232, 231, 0.1)' : 'transparent',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  padding: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'translateX(4px)',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const GlassNotificationAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  color: GlassColors.secondary,
}));

export const GlassNotificationTitle = styled(Typography)(({ theme }) => ({
  ...GlassTypography.button,
  fontSize: '14px',
  fontWeight: 600,
  color: GlassColors.textPrimary,
  marginBottom: theme.spacing(0.5),
}));

export const GlassNotificationText = styled(Typography)(({ theme }) => ({
  ...GlassTypography.body,
  fontSize: '13px',
  color: GlassColors.textSecondary,
  lineHeight: 1.4,
}));

export const GlassNotificationTime = styled(Typography)(({ theme }) => ({
  ...GlassTypography.body,
  fontSize: '12px',
  color: GlassColors.textMuted,
  marginTop: theme.spacing(0.5),
}));

// ============================================================================
// SCROLL UTILITIES AND MIXINS
// ============================================================================

// Standardized scroll styles for consistent scrollbars across the app
export const GlassScrollStyles = {
  // Standard glass scrollbar
  standard: {
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 92, 173, 0.3)',
      borderRadius: '4px',
      transition: 'background 0.3s ease',
      '&:hover': {
        background: 'rgba(0, 92, 173, 0.5)',
      },
    },
    // Firefox
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'rgba(0, 92, 173, 0.3) rgba(255, 255, 255, 0.1)',
  },

  // Thin scrollbar variant
  thin: {
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 92, 173, 0.3)',
      borderRadius: '3px',
      transition: 'background 0.3s ease',
      '&:hover': {
        background: 'rgba(0, 92, 173, 0.5)',
      },
    },
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'rgba(0, 92, 173, 0.3) rgba(255, 255, 255, 0.1)',
  },

  // Accent colored scrollbar
  accent: {
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 232, 231, 0.3)',
      borderRadius: '4px',
      transition: 'background 0.3s ease',
      '&:hover': {
        background: 'rgba(0, 232, 231, 0.5)',
      },
    },
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'rgba(0, 232, 231, 0.3) rgba(255, 255, 255, 0.1)',
  },

  // Hidden scrollbar but still scrollable
  hidden: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
  },
};

// ============================================================================
// SCROLL CONTAINERS
// ============================================================================

export const GlassScrollContainer = styled(Box)<{
  variant?: 'standard' | 'thin' | 'accent' | 'hidden';
  maxHeight?: string | number;
}>(({ theme, variant = 'standard', maxHeight = 'auto' }) => ({
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  maxHeight,
  ...GlassScrollStyles[variant],
}));

export const GlassScrollableCard = styled(GlassCard)<{
  scrollVariant?: 'standard' | 'thin' | 'accent' | 'hidden';
  maxHeight?: string | number;
}>(({ theme, scrollVariant = 'standard', maxHeight = '400px' }) => ({
  maxHeight,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  ...GlassScrollStyles[scrollVariant],
}));

// ============================================================================
// RESPONSIVE SCROLL BEHAVIORS
// ============================================================================

export const GlassResponsiveScroll = styled(Box)<{
  mobileMaxHeight?: string | number;
  desktopMaxHeight?: string | number;
  scrollVariant?: 'standard' | 'thin' | 'accent' | 'hidden';
}>(
  ({
    theme,
    mobileMaxHeight = '300px',
    desktopMaxHeight = '500px',
    scrollVariant = 'standard',
  }) => ({
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxHeight: mobileMaxHeight,
    ...GlassScrollStyles[scrollVariant],
    [theme.breakpoints.up('sm')]: {
      maxHeight: desktopMaxHeight,
    },
    // Smooth scrolling
    scrollBehavior: 'smooth',
    // Prevent scroll momentum bouncing on iOS
    overscrollBehavior: 'contain',
  }),
);

// ============================================================================
// MODAL AND DIALOG SCROLL CONTAINERS
// ============================================================================

export const GlassModalScrollContainer = styled(Box)(({ theme }) => ({
  maxHeight: '80vh',
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  padding: theme.spacing(1),
  ...GlassScrollStyles.thin,
  // Ensure modal content doesn't overflow
  overscrollBehavior: 'contain',
}));

// ============================================================================
// LIST SCROLL CONTAINERS
// ============================================================================

export const GlassListScrollContainer = styled(Box)<{
  itemHeight?: number;
  maxVisibleItems?: number;
}>(({ theme, itemHeight = 56, maxVisibleItems = 6 }) => ({
  maxHeight: itemHeight * maxVisibleItems,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  ...GlassScrollStyles.standard,
  // Snap scrolling for better UX
  scrollSnapType: 'y mandatory',
  '& > *': {
    scrollSnapAlign: 'start',
  },
}));

// ============================================================================
// UTILITY FUNCTIONS FOR SCROLL MANAGEMENT
// ============================================================================

// Utility to scroll element into view with smooth behavior
export const scrollIntoView = (
  element: HTMLElement | null,
  options?: ScrollIntoViewOptions,
) => {
  if (!element) return;

  const defaultOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  };

  element.scrollIntoView({ ...defaultOptions, ...options });
};

// Utility to check if element is in viewport
export const isElementInViewport = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Utility to get scroll position
export const getScrollPosition = (
  element: HTMLElement | Window = window,
): { x: number; y: number } => {
  if (element === window) {
    return {
      x: window.pageXOffset || document.documentElement.scrollLeft,
      y: window.pageYOffset || document.documentElement.scrollTop,
    };
  }

  const el = element as HTMLElement;
  return {
    x: el.scrollLeft,
    y: el.scrollTop,
  };
};

// Utility to smooth scroll to position
export const smoothScrollTo = (
  element: HTMLElement | Window,
  x: number,
  y: number,
  duration: number = 300,
): void => {
  const startTime = performance.now();
  const startPos = getScrollPosition(element);

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out cubic)
    const easing = 1 - Math.pow(1 - progress, 3);

    const currentX = startPos.x + (x - startPos.x) * easing;
    const currentY = startPos.y + (y - startPos.y) * easing;

    if (element === window) {
      window.scrollTo(currentX, currentY);
    } else {
      const el = element as HTMLElement;
      el.scrollLeft = currentX;
      el.scrollTop = currentY;
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

// ============================================================================
// NEW COMPONENT VARIANTS FOR REDESIGN
// ============================================================================

// Header Container - Blue background with navigation layout
// Accept isLoggedIn prop to adjust width
export const GlassHeaderContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLoggedIn' && prop !== 'isMenuOpened',
})<{ isLoggedIn?: boolean; isMenuOpened?: boolean }>(({
  theme,
  isLoggedIn,
  isMenuOpened,
}) => {
  const backgroundColor = '#00243ACC';
  const isLight = theme.palette.mode === 'light';

  return {
    width: '956px',
    maxWidth: 'round(nearest, 95%, 1px)',
    height: '64px',
    backgroundColor: isLight
      ? backgroundColor
      : GlassColors.dark.iconBtnBackground,
    borderRadius: '64px',
    padding: '12px 12px 12px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: isLoggedIn ? '60px' : '24px',
    boxShadow:
      '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
    backdropFilter: 'blur(6px)',
    margin: '0 auto',
    transition: 'all .2s ease',
    [theme.breakpoints.down('lg')]: {},
    [theme.breakpoints.down('md')]: {
      gap: '16px',
    },
    [theme.breakpoints.down('tablet')]: {
      padding: '12px 20px',
      backgroundColor: isMenuOpened
        ? 'transparent'
        : isLight
          ? backgroundColor
          : GlassColors.dark.iconBtnBackground,
      backdropFilter: isMenuOpened ? 'blur(0)' : 'blur(6px)',
      boxShadow: isMenuOpened
        ? 'none'
        : '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
      height: isMenuOpened ? 'auto' : '48px',
      borderRadius: isMenuOpened ? '32px 32px 0 0' : '64px',
      // padding: isMenuOpened ? '24px 20px 0 24px' : '8px 8px 8px 16px',
    },
  };
});

// Search Input - Specific styling for hero search
export const GlassSearchInput = styled(TextField)(({ theme }) => ({
  width: '760px',
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    height: '53px',
    backgroundColor: 'rgba(153, 153, 153, 0.3)',
    borderRadius: '64px',
    border: 'none',
    fontSize: '16px',
    fontFamily: 'Roboto, sans-serif',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px 24px',
    '&::placeholder': {
      color: '#585858',
      opacity: 1,
      fontFamily: 'Roboto, sans-serif',
    },
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

// Service Card - Category card with proper dimensions
export const GlassServiceCard = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight
    ? GlassColors.light.primaryBackground
    : GlassColors.dark.primaryBackground;

  return {
    width: '297.33px',
    minWidth: '248px',
    height: '284px',
    minHeight: '248px',
    backgroundColor: bgColor,
    borderRadius: '64px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: GlassShadows.glassUp,
    '&:hover': {
      backgroundColor: 'rgba(0, 232, 231, 0.1)',
      transform: 'scale(1.05)',
      boxShadow: `${GlassShadows.glassUp}, 0 10px 20px rgba(0, 232, 231, 0.2)`,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'auto',
      height: '220px',
      minHeight: '220px',
      borderRadius: '32px',
    },
  };
});

// ============================================================================
// NEUMORPHIC BUTTON STYLES
// ============================================================================

// Neumorphic Indented Button - Primary Action Button
export const NeumorphicButton = styled(Button)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';

  return {
    background: isLight ? 'rgba(0, 36, 58, 0.8)' : 'rgba(0, 92, 173, 0.7)', // Dark teal/blue-grey background
    color: '#FFFFFF',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: '64px',
    padding: '8px 24px',
    height: '48px',
    minWidth: '104px',
    fontSize: '12px',
    lineHeight: '24px',

    [theme.breakpoints.up('sm')]: {
      fontSize: '16px',
      lineHeight: '32px',
      minWidth: '66px',
    },
    // Neumorphic indented effect - button appears pressed into surface
    boxShadow: `
      0px 0px 16px 0px rgba(242, 242, 242, 0.1) inset,
      0px 0px 3px 0px rgba(255, 255, 255, 0.3) inset,
      -1px -1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
      1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
      -1px -1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
      1px 1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
      0px 1px 8px 0px rgba(0, 0, 0, 0.12),
      0px 0px 2px 0px rgba(0, 0, 0, 0.1)
    `,
    border: 0,
    '&:hover': {
      boxShadow: `
        0px 0px 16px 0px rgba(0, 23, 62, 0.4) inset,
        0px 0px 3px 0px rgba(0, 73, 137, 0.5) inset,
        -1px -1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
        1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
        -1px -1px 0px -0.5px rgba(255, 255, 255, 0.3) inset,
        1px 1px 0px -0.5px rgba(255, 255, 255, 0.2) inset,
        0px 1px 8px 0px rgba(0, 255, 255, 0.2),
        0px 0px 2px 0px rgba(0, 0, 0, 0.1)
      `,
      backgroundColor: isLight
        ? 'rgba(0, 36, 58, 0.8)'
        : 'rgba(0, 92, 173, 0.7)', // Slightly darker on hover
    },
    '&:active': {
      color: GlassColors.accent,
      backgroundColor: isLight
        ? 'rgba(0, 36, 58, 0.8)'
        : 'rgba(0, 92, 173, 0.7)', // Even darker when pressed
    },
    '&:disabled': {
      backgroundColor: 'rgba(59, 59, 59, 0.18)',
      boxShadow: `
        1px 1px 0.5px -1px rgba(255, 255, 255, 0.5) inset,
        -1px -1px 0px -0.5px rgba(38, 38, 38, 0.6) inset,
        0px 0px 2px 0px rgba(0, 0, 0, 0.1)
      `,
    },
  };
});

// Neumorphic Raised Container - For search inputs, cards, etc.
export const NeumorphicContainer = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';

  return {
    backgroundColor: GlassColors.tertiary, // Light beige background
    borderRadius: '64px',
    // Neumorphic raised effect - container appears extruded from background
    boxShadow: `
      0px 0px 10px 0px rgba(0, 0, 0, 0.04),
      0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.3),
      0px 0.5px 0.75px 0px rgba(255, 255, 255, 1),
      0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.27) inset,
      0px 1.08px 2.48px 0px rgba(0, 0, 0, 0.25) inset
    `,
    // border: "1px solid rgba(255, 255, 255, 0.3)",
    padding: '4px 24px',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',

    '&::placeholder': {
      color: isLight ? 'rgba(26, 26, 26, 1)' : 'rgba(213, 213, 213, 1)',
    },
  };
});

// Neumorphic Raised Button - For category buttons and other raised elements
export const NeumorphicRaisedButton = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';

  return {
    backgroundColor: '#F5F5F0', // Light beige background
    borderRadius: '32px',
    padding: '8px 24px',
    minHeight: '64px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    // Neumorphic raised effect - button appears extruded from background
    boxShadow: `
      4px 4px 8px rgba(0, 0, 0, 0.1),
      -4px -4px 8px rgba(255, 255, 255, 0.8),
      inset 1px 1px 2px rgba(255, 255, 255, 0.6),
      inset -1px -1px 2px rgba(0, 0, 0, 0.1)
    `,
    border: '1px solid rgba(255, 255, 255, 0.3)',
    '&:hover': {
      boxShadow: `
        6px 6px 12px rgba(0, 0, 0, 0.12),
        -6px -6px 12px rgba(255, 255, 255, 0.9),
        inset 1px 1px 2px rgba(255, 255, 255, 0.7),
        inset -1px -1px 2px rgba(0, 0, 0, 0.15)
      `,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      boxShadow: `
        2px 2px 4px rgba(0, 0, 0, 0.1),
        -2px -2px 4px rgba(255, 255, 255, 0.8),
        inset 1px 1px 2px rgba(0, 0, 0, 0.1),
        inset -1px -1px 2px rgba(255, 255, 255, 0.6)
      `,
      transform: 'translateY(1px)',
    },
  };
});

// Service Card Icon
export const GlassServiceIcon = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  boxShadow: `
    inset 2px 2px 5px rgba(0, 0, 0, 0.1),
    inset -2px -2px 5px rgba(255, 255, 255, 0.3)
  `,
  '& svg, & .icon': {
    fontSize: '32px',
    color: '#00E8E7',
  },
}));

// Theme Toggle - Circular toggle with icon positioning
export const GlassThemeToggle = styled(Box)(({ theme }) => ({
  width: '58px',
  height: '28px',
  borderRadius: '14px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  padding: '2px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

export const GlassThemeToggleButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDark',
})<{ isDark?: boolean }>(({ theme, isDark }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  left: isDark ? '30px' : '2px',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '16px',
    color: '#00FFFF',
  },
}));

// Footer Section - Three-column layout support
export const GlassFooterContainer = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight ? '#E5E5DC' : '#1A1A1A';

  return {
    width: '956px',
    maxWidth: '100%',
    backgroundColor: bgColor,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      height: 'auto',
    },
  };
});

export const GlassFooterColumns = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '32px',
  padding: '24px 48px',
  borderRadius: '32px',
  boxShadow: GlassShadows.glassDown,
  flex: 1,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
    marginBottom: '16px',
  },
}));

export const GlassFooterColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

export const GlassFooterColumnTitle = styled(Typography)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  return {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '16px',
    fontWeight: 700,
    color: isLight ? '#005CAD' : '#00FFFF',
  };
});

export const GlassFooterColumnLink = styled(Typography, {
  shouldForwardProp: (prop) => !['component', 'href'].includes(prop as string),
})<{
  component?: React.ElementType;
  href?: string;
}>(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const textColor = isLight ? '#1A1A1A' : '#B6B6B6';

  return {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    color: textColor,
    cursor: 'pointer',
    transition: 'color 0.3s ease',
    textDecoration: 'none',
    '&:hover': {
      color: isLight ? '#005CAD' : '#00FFFF',
    },
  };
});

export const GlassFooterBottom = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '48px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '16px',
    marginLeft: 0,
    marginRight: 0,
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: '24px',
  },
}));

export const GlassFooterBranding = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
    gap: '10px',
  },
}));

export const GlassFooterLogo = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  return {
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: isLight ? '#005CAD' : '#00FFFF',
    '& img': {
      filter: isLight
        ? 'none'
        : 'brightness(0) saturate(100%) invert(67%) sepia(100%) saturate(2476%) hue-rotate(135deg) brightness(101%) contrast(101%)',
    },
  };
});

export const GlassFooterCompanyText = styled(Typography)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const companyTextColor = isLight ? '#005CAD' : '#00E8E7'; // Blue in light, cyan in dark

  return {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '20px',
    fontWeight: 800,
    color: companyTextColor,
    letterSpacing: '0.15px',
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
      fontWeight: 900,
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  };
});

export const GlassSocialIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
}));

export const GlassSocialIconButton = styled(IconButton)(({ theme }) => {
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight ? 'rgba(0, 36, 58, 0.8)' : 'rgba(0, 92, 173, 0.7)';

  return {
    width: '40px',
    height: '40px',
    color: '#E2F7FF',
    borderRadius: '50%',
    border: 0,
    boxShadow:
      '0px 0px 16px 0px rgba(242, 242, 242, 0.1) inset, 0px 0px 3px 0px rgba(255, 255, 255, 0.3) inset, -1px -1px 0.5px -1px rgba(255, 255, 255, 0.4) inset, 1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,  -1px -1px 0px -0.5px rgba(255, 255, 255, 0.2) inset, 1px 1px 0px -0.5px rgba(255, 255, 255, 0.2) inset, 0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 0px 2px 0px rgba(0, 0, 0, 0.1)',
    filter: 'none',
    transition: 'all 0.3s ease',
    '&': {
      backgroundColor: bgColor,
    },
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: 'none !important',
      WebkitBoxShadow: 'none !important',
      MozBoxShadow: 'none !important',
      filter: 'none',
      backgroundColor: bgColor,
    },
    '&:focus': {
      boxShadow: 'none !important',
      WebkitBoxShadow: 'none !important',
      MozBoxShadow: 'none !important',
      backgroundColor: bgColor,
    },
    '& svg': {
      fontSize: '20px',
      filter: 'none',
      textShadow: 'none',
      boxShadow: 'none',
      borderRadius: '50%',
    },
  };
});
